import React, { useMemo, useState } from "react";
import { Snackbar, Alert } from "@mui/material";
import { Avatar, InviteStatusPill, SourcePill, formatInvitedDate } from "./sharedUI";

const StatCard = ({ label, value }) => (
    <div className="icv2-stat-card">
        <div className="icv2-stat-value">{value}</div>
        <div className="icv2-stat-label">{label}</div>
    </div>
);

// Normalises an invite record from GET /jobs/{job_id}/candidate-invites/
// (invites[] with nested status + candidate) into a flat row.
const mapInvite = (inv) => {
    const c = inv.candidate || {};
    const status = inv.status || {};
    return {
        id: inv.id || inv.invite_id,
        name: c.full_name || c.name || inv.candidate_name || "Unknown",
        email: c.email || inv.candidate_email || "",
        phone: c.phone_number || c.phone || inv.candidate_phone || "",
        title: c.current_designation || c.title || c.headline || "",
        invitedAt: inv.created_at || inv.invited_at || inv.updated_at || null,
        statusLabel: status.name || status.label || inv.status_name || "Invited",
        statusSlug: status.slug || status.code || inv.status_slug || "",
        statusColor: status.color || status.color_code || null,
        source: inv.source || "smart_search",
    };
};

const statusKey = (row) =>
    (row.statusSlug || row.statusLabel || "").toString().toLowerCase().replace(/\s+/g, "_");

// Resend is allowed for Invited / No Response only.
const canResend = (row) => ["invited", "invite_sent", "no_response"].includes(statusKey(row));

const InvitedCandidatesTab = ({
    invites = [],
    summary = { invited: 0, applied: 0, no_response: 0, total: 0 },
    loading = false,
    hiringStatuses = [],
    page = 1,
    pageSize = 20,
    statusId = "",
    pagination = null,
    onPageChange,
    onPageSizeChange,
    onStatusChange,
    onResend,
    onRefresh,
}) => {
    const [search, setSearch] = useState("");
    const [sortAsc, setSortAsc] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        let list = invites.map(mapInvite).filter((r) => {
            if (!term) return true;
            return r.name.toLowerCase().includes(term) || r.email.toLowerCase().includes(term);
        });
        list.sort((a, b) => {
            const diff = new Date(a.invitedAt || 0) - new Date(b.invitedAt || 0);
            return sortAsc ? diff : -diff;
        });
        return list;
    }, [invites, search, sortAsc]);

    const total = pagination?.total ?? summary?.total ?? invites.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, totalPages);

    const handleResend = async (row) => {
        if (!onResend || resendingId) return;
        setResendingId(row.id);
        const res = await onResend({ id: row.id });
        setResendingId(null);
        setSnackbar({
            open: true,
            message: res?.success
                ? `Invite resent to ${row.name}.`
                : res?.message || "Failed to resend invite.",
            severity: res?.success ? "success" : "error",
        });
    };

    const renderPagination = () => {
        const pages = [];
        pages.push(
            <button key="prev" className="icv2-page-btn" onClick={() => onPageChange?.(currentPage - 1)} disabled={currentPage === 1}>
                ‹
            </button>
        );
        pages.push(
            <button key={1} className={`icv2-page-btn${currentPage === 1 ? " active" : ""}`} onClick={() => onPageChange?.(1)}>1</button>
        );
        if (currentPage > 3) pages.push(<span key="ldots" className="icv2-page-ellipsis">…</span>);
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        for (let i = start; i <= end; i++) {
            pages.push(
                <button key={i} className={`icv2-page-btn${currentPage === i ? " active" : ""}`} onClick={() => onPageChange?.(i)}>{i}</button>
            );
        }
        if (currentPage < totalPages - 2) pages.push(<span key="rdots" className="icv2-page-ellipsis">…</span>);
        if (totalPages > 1) {
            pages.push(
                <button key={totalPages} className={`icv2-page-btn${currentPage === totalPages ? " active" : ""}`} onClick={() => onPageChange?.(totalPages)}>{totalPages}</button>
            );
        }
        pages.push(
            <button key="next" className="icv2-page-btn" onClick={() => onPageChange?.(currentPage + 1)} disabled={currentPage === totalPages}>
                ›
            </button>
        );
        return pages;
    };

    return (
        <div className="icv2-invited-tab">
            <div className="icv2-invited-header">
                <div className="icv2-invited-header-text">
                    <span className="icv2-eyebrow">Outreach overview</span>
                    <h2>Invited candidates</h2>
                    <p>Stay close to every conversation, from first send to application.</p>
                </div>
                <div className="icv2-stat-cards">
                    <StatCard label="Invited" value={summary?.invited ?? 0} />
                    <StatCard label="Applied" value={summary?.applied ?? 0} />
                    <StatCard label="No response" value={summary?.no_response ?? 0} />
                    <StatCard label="Total" value={summary?.total ?? 0} />
                </div>
            </div>

            <div className="icv2-invited-toolbar">
                <div className="icv2-search-box">
                    <img src="/images/search.svg" alt="" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search invited candidates…"
                    />
                </div>
                <div className="icv2-toolbar-right">
                    <span className="icv2-showing-label">Showing</span>
                    <select
                        className="icv2-status-select"
                        value={statusId}
                        onChange={(e) => onStatusChange?.(e.target.value)}
                    >
                        <option value="">All</option>
                        {hiringStatuses.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                    <button
                        className="icv2-sort-toggle"
                        onClick={() => setSortAsc((v) => !v)}
                        title={sortAsc ? "Oldest first" : "Newest first"}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: sortAsc ? "rotate(180deg)" : "none" }}>
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    <button className="icv2-sort-toggle" onClick={() => onRefresh?.()} title="Refresh">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10" />
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                        </svg>
                    </button>
                </div>
            </div>

            <div className="icv2-invited-table-wrap">
                <table className="icv2-invited-table">
                    <thead>
                        <tr>
                            <th>Candidate</th>
                            <th>Contact</th>
                            <th>Source</th>
                            <th>Invited</th>
                            <th>Status</th>
                            <th className="icv2-th-actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="icv2-table-empty">Loading invited candidates…</td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="icv2-table-empty">
                                    No invited candidates yet. Add candidates from the &ldquo;Add candidates&rdquo; tab.
                                </td>
                            </tr>
                        ) : (
                            rows.map((c) => (
                                <tr key={c.id}>
                                    <td>
                                        <div className="icv2-candidate-cell">
                                            <Avatar name={c.name} size={34} />
                                            <div>
                                                <strong>{c.name}</strong>
                                                {c.title && <p>{c.title}</p>}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{c.email || "—"}</td>
                                    <td><SourcePill source={c.source} /></td>
                                    <td>{formatInvitedDate(c.invitedAt)}</td>
                                    <td><InviteStatusPill label={c.statusLabel} slug={c.statusSlug} color={c.statusColor} /></td>
                                    <td className="icv2-th-actions">
                                        <div className="icv2-row-actions">
                                            {canResend(c) && (
                                                <button
                                                    className="icv2-icon-btn"
                                                    title="Resend invite"
                                                    disabled={resendingId === c.id}
                                                    onClick={() => handleResend(c)}
                                                >
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="23 4 23 10 17 10" />
                                                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="icv2-pagination">
                <span className="icv2-pagination-info">
                    {total ? (
                        <>
                            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, total)} of{" "}
                            <strong>{total}</strong> candidates
                        </>
                    ) : (
                        "Showing 0 candidates"
                    )}
                </span>
                <div className="icv2-pagination-right">
                    <div className="icv2-page-btns">{renderPagination()}</div>
                    <div className="icv2-per-page">
                        <span>Per page:</span>
                        <select
                            value={pageSize}
                            onChange={(e) => onPageSizeChange?.(parseInt(e.target.value))}
                        >
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={closeSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default InvitedCandidatesTab;
